import { Box } from '@mui/material';
import { MouseEvent } from 'react';

const BANNER_NATIVE_W = 1200;
const BANNER_NATIVE_H = 251;

/**
 * Provider attribution logos in a fixed aspect slot (native wide assets are
 * 1200×251). The outer link and the image share one height so compact
 * standalone `alertwest*.png` is not wrapped in a taller box than the bitmap.
 * Camera `attribution`; when it is `ALERTWEST`, the slot uses `COMPACT_ALERTWEST_SLOT_HEIGHT_PX`,
 * otherwise `SLOT_HEIGHT_PX`. Slot width scales with slot height.
 */
const SLOT_HEIGHT_PX = 100;
/** Shorter slot for ALERTWest-only cameras; tune if standalone/composite logos change. */
const COMPACT_ALERTWEST_SLOT_HEIGHT_PX = 60;

export type CameraAttributionLogoProps = {
  src: string;
  alt: string;
  href: string;
  /** When `ALERTWEST`, uses compact slot for standalone west logo. */
  attribution?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
};

export const CameraAttributionLogo = (
  props: CameraAttributionLogoProps,
): JSX.Element => {
  const { src, alt, href, attribution, onClick } = props;

  const slotHeightPx =
    attribution?.toUpperCase() === 'ALERTWEST'
      ? COMPACT_ALERTWEST_SLOT_HEIGHT_PX
      : SLOT_HEIGHT_PX;

  const slotMaxWidth = `min(100%, calc(${slotHeightPx}px * ${BANNER_NATIVE_W} / ${BANNER_NATIVE_H}))`;

  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        height: `${slotHeightPx}px`,
        width: slotMaxWidth,
        maxWidth: '100%',
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          maxWidth: '100%',
          maxHeight: `${slotHeightPx}px`,
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
        }}
      />
    </Box>
  );
};
